import { reportException } from '_lib/utils/sentryService';

import {
  CreateResource,
  CustomQueryHookResult,
  GetByIdResource,
  ListHookResult,
  ListOptions,
  ListResource,
  UnpagedListOptions,
  UseQueryOptions,
  createApiResource,
  useCustomQuery
} from '../apiResourceFactory';
import { API } from '../apiService';
import {
  ListResult,
  PhoneCallDetailDTO,
  PhoneCallListDTO,
  PhoneCallQueryParams,
  PointmanPhoneCallCreateDTO
} from '../apiTypes.shim';

type CustomerCallHistoryParams = Omit<PhoneCallQueryParams, 'serializer'>;

export interface PhoneCallV1Resource
  extends ListResource<PhoneCallListDTO, PhoneCallQueryParams>,
    GetByIdResource<PhoneCallDetailDTO>,
    CreateResource<PointmanPhoneCallCreateDTO, PhoneCallDetailDTO> {
  useCustomerCallHistoryCached: (
    options: ListOptions<CustomerCallHistoryParams, { requireParams: true }>
  ) => ListHookResult<PhoneCallDetailDTO>;
  useCount: (
    options: UnpagedListOptions<PhoneCallQueryParams, { requireParams: true }>,
    queryConfig?: UseQueryOptions<number>
  ) => CustomQueryHookResult<number>;
  listFull: (options: ListOptions<PhoneCallQueryParams>) => Promise<ListResult<PhoneCallDetailDTO>>;
}

export const phoneCallUri = `phone-call`;

export const createPhoneCallV1Resource = (): PhoneCallV1Resource => {
  const { useList, list, listQueryKey, getById, useGetById, getByIdQueryKey, create, useCreate } = createApiResource(
    API,
    phoneCallUri
  );

  const useCount = (
    options: UnpagedListOptions<PhoneCallQueryParams, { requireParams: true }>,
    queryConfig?: UseQueryOptions<number>
  ) =>
    useCustomQuery({
      queryKey: [phoneCallUri, 'count', { params: options.params }],
      queryFn: async () => {
        try {
          const res = await API.get(`${phoneCallUri}/count`, {
            ...options,
            extraOptions: { retry: 1, ...options?.extraOptions }
          });
          return res.data.count;
        } catch (err) {
          if (!options?.disableErrorReporting) reportException(err);
          return 0;
        }
      },
      ...queryConfig
    });

  return {
    useCustomerCallHistoryCached: (options) =>
      useList(
        { ...options, params: { pageSize: 10, ...options.params, serializer: 'full' } },
        { refetchOnMount: false }
      ),
    useList,
    list,
    listFull: (options) => list({ ...options, params: { ...options?.params, serializer: 'full' } }),
    listQueryKey,
    getById,
    useGetById,
    getByIdQueryKey,
    useCount,
    create,
    useCreate
  };
};
